import React from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { AlertCircle, CheckCircle, ChevronDown, ChevronUp, Eye, FileCheck2, KeyRound, Lock, RefreshCcw, ShieldCheck, Wrench } from 'lucide-react';
import { DeploymentTargetRecord, ManagedArtifactSnapshot, SetupTargetSummary } from '../../types/deployment';
import { formatDeploymentModeLabel } from '../../utils/deploymentMode';
import { SetupHistoryPanel, SetupRunHistoryState } from './SetupHistoryPanel';
import {
  TARGET_SETUP_STATUS_CONFIG,
  SETUP_STATUS_LABELS,
  canTargetReprovisionCredentials,
  formatDateTime,
  formatFailureClassSummary,
  formatSetupActionLabel,
  formatTlsSummary,
  formatTlsTooltip,
  getSetupActivityAt,
  getTargetCredentialStatus,
  getTargetFailureClass,
  getTargetFailureMessage,
  getTargetPreferredSetupMode,
  getTargetTlsStatus,
  getTlsChipColor,
  getTlsChipLabel,
  getLockTargetLabels,
  shouldShowTlsStatus,
} from './setupExplorerUtils';

interface CurrentSetupRowProps {
  summary: SetupTargetSummary;
  saving?: boolean;
  isAdmin: boolean;
  historyOpen: boolean;
  historyState?: SetupRunHistoryState;
  onToggleHistory: () => void;
  onLoadMoreHistory: () => void;
  onRefreshHistory: () => void;
  onOpenSetup: (target: DeploymentTargetRecord) => void;
  onReprovision: (target: DeploymentTargetRecord) => void;
  onOpenManagedArtifacts: (target: DeploymentTargetRecord) => void;
  onTlsAction: (target: DeploymentTargetRecord, action: 'check' | 'issue' | 'renew-dry-run' | 'renew') => void;
  onRefreshSetup: (setupId: string) => void;
  onOpenRunDetails: (setup: NonNullable<SetupTargetSummary['latestSetup']>) => void;
}

export const CurrentSetupRow: React.FC<CurrentSetupRowProps> = ({
  summary,
  saving = false,
  isAdmin,
  historyOpen,
  historyState,
  onToggleHistory,
  onLoadMoreHistory,
  onRefreshHistory,
  onOpenSetup,
  onReprovision,
  onOpenManagedArtifacts,
  onTlsAction,
  onRefreshSetup,
  onOpenRunDetails,
}) => {
  const { target, latestSetup, activeLocks, historyCount } = summary;
  const setupState = target.setupState;
  const statusCfg = TARGET_SETUP_STATUS_CONFIG[setupState?.status ?? 'not-started'] ?? TARGET_SETUP_STATUS_CONFIG['not-started'];
  const isSelectable = (target.allowedDeploymentModes?.length ?? 0) > 1;
  const canReprovision = canTargetReprovisionCredentials(target);
  const latestFailureClass = getTargetFailureClass(target, latestSetup);
  const latestFailureMessage = getTargetFailureMessage(target, latestSetup);
  const credentialStatus = getTargetCredentialStatus(target, latestSetup);
  const tlsStatus = getTargetTlsStatus(target, latestSetup);
  const tlsConfigured = (
    target.metadata?.setupTlsEnabled === true
    || String(target.metadata?.setupTlsEnabled || '').toLowerCase() === 'true'
    || Boolean(String(target.metadata?.setupDomain || '').trim())
  );
  const displayedTlsStatus = tlsStatus || (tlsConfigured ? {
    managed: true,
    state: '' as const,
    domain: String(target.metadata?.setupDomain || ''),
    summary: 'TLS is configured but has not been checked.',
  } : null);
  const tlsSummary = formatTlsSummary(displayedTlsStatus);
  const metadataManagedArtifactValue = target.metadata?.managedArtifacts;
  const metadataManagedArtifacts = Array.isArray(metadataManagedArtifactValue)
    ? metadataManagedArtifactValue as ManagedArtifactSnapshot[]
    : [];
  const persistedManagedArtifacts = setupState?.managedArtifacts ?? metadataManagedArtifacts;
  const managedArtifactStatuses = summary.managedArtifacts ?? [];
  const managedArtifactStates: string[] = managedArtifactStatuses.length > 0
    ? managedArtifactStatuses.map((artifact) => artifact.status)
    : persistedManagedArtifacts.map((artifact) => artifact.status);
  const managedArtifactsReady = managedArtifactStates.length > 0
    && managedArtifactStates.every((status) => ['active', 'ready', 'current', 'success'].includes(status));
  const managedArtifactsRequireAdmin = managedArtifactStates.includes('requires-admin');
  const hasExistingSetup = ['ready', 'ready-for-update', 'partially-ready', 'failed', 'failed-preflight'].includes(
    setupState?.status ?? ''
  );
  const lockSummary = activeLocks[0] ?? null;
  const lockTargetLabels = lockSummary ? getLockTargetLabels(lockSummary) : [];
  const preferredSetupMode = getTargetPreferredSetupMode(target);

  return (
    <Card variant="outlined" sx={{ px: 2, py: 1.5 }}>
      <Stack spacing={1.5}>
        <Stack spacing={1.5} sx={{ minWidth: 0 }}>
          <Stack spacing={0.75} sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
              <Typography variant="body1" fontWeight={700}>
                {target.name}
              </Typography>
              <Chip label={target.environment} size="small" variant="outlined" />
              {isSelectable && preferredSetupMode ? (
                <Chip
                  label={`Setup: ${formatDeploymentModeLabel(preferredSetupMode)}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ) : null}
              <Chip label={statusCfg.label} size="small" color={statusCfg.color} />
              {historyCount > 0 ? (
                <Chip size="small" variant="outlined" label={`${historyCount} historical`} />
              ) : null}
              {activeLocks.length > 0 ? (
                <Tooltip title={lockSummary ? `Locked by ${lockSummary.owner?.name || lockSummary.owner?.email || 'another operator'}${lockTargetLabels.length > 0 ? ` for ${lockTargetLabels.join(', ')}` : ''}` : 'Another operation is running on this target'}>
                  <Chip icon={<Lock size={12} />} label="Locked" size="small" color="warning" />
                </Tooltip>
              ) : null}
              {credentialStatus ? (
                <Tooltip title={credentialStatus.detail}>
                  <Chip
                    icon={<KeyRound size={12} />}
                    label={credentialStatus.label}
                    size="small"
                    color={credentialStatus.color}
                    variant="outlined"
                  />
                </Tooltip>
              ) : null}
              {shouldShowTlsStatus(displayedTlsStatus) ? (
                <Tooltip title={formatTlsTooltip(displayedTlsStatus)}>
                  <Chip
                    icon={getTlsChipColor(displayedTlsStatus) === 'success' ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
                    label={getTlsChipLabel(displayedTlsStatus)}
                    size="small"
                    color={getTlsChipColor(displayedTlsStatus)}
                    variant="outlined"
                  />
                </Tooltip>
              ) : null}
              {managedArtifactStates.length > 0 ? (
                <Tooltip title={`${managedArtifactStates.length} managed file ${managedArtifactStates.length === 1 ? 'set' : 'sets'}`}>
                  <Chip
                    icon={<FileCheck2 size={12} />}
                    label={managedArtifactsRequireAdmin
                      ? 'Managed files require admin'
                      : managedArtifactsReady
                        ? 'Managed files ready'
                        : 'Managed files need attention'}
                    size="small"
                    color={managedArtifactsRequireAdmin ? 'error' : managedArtifactsReady ? 'success' : 'warning'}
                    variant="outlined"
                  />
                </Tooltip>
              ) : null}
            </Stack>

            <Typography variant="body2" color="text.secondary">
              {typeof target.repository === 'string' ? target.repository : target.repository?.pathWithNamespace || target.repository?.name || '—'}
              {latestSetup
                ? ` • Last setup action: ${formatSetupActionLabel(latestSetup.actionType)} (${SETUP_STATUS_LABELS[latestSetup.status]}) • ${formatDateTime(getSetupActivityAt(latestSetup))}`
                : ' • No setup actions yet'}
            </Typography>

            {setupState?.lastFailedStep ? (
              <Typography variant="caption" color="error.main">
                Failed step: {setupState.lastFailedStep}
              </Typography>
            ) : null}
            {latestFailureClass ? (
              <Typography variant="caption" color="warning.main" display="block">
                Failure: {formatFailureClassSummary(latestFailureClass)}
                {latestFailureClass ? ` (${latestFailureClass})` : ''}
              </Typography>
            ) : null}
            {latestFailureMessage ? (
              <Typography variant="caption" color="text.secondary" display="block">
                {latestFailureMessage}
              </Typography>
            ) : null}
            {tlsSummary ? (
              <Typography
                variant="caption"
                color={getTlsChipColor(displayedTlsStatus) === 'warning' ? 'warning.main' : 'text.secondary'}
                display="block"
              >
                TLS: {tlsSummary}
              </Typography>
            ) : null}
            {latestSetup?.currentStep || latestSetup?.failedStep ? (
              <Typography variant="caption" color="text.secondary" display="block">
                Current step: {latestSetup?.currentStep || latestSetup?.failedStep}
              </Typography>
            ) : null}
          </Stack>

          <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<FileCheck2 size={14} />}
              onClick={() => onOpenManagedArtifacts(target)}
              disabled={saving || activeLocks.length > 0}
            >
              Managed Files
            </Button>
            {isAdmin ? (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ShieldCheck size={14} />}
                  onClick={() => onTlsAction(target, 'check')}
                  disabled={saving || activeLocks.length > 0}
                >
                  Check TLS
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => onTlsAction(target, 'issue')}
                  disabled={saving || activeLocks.length > 0}
                >
                  Issue TLS
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => onTlsAction(target, 'renew-dry-run')}
                  disabled={saving || activeLocks.length > 0}
                >
                  Renewal Dry Run
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => onTlsAction(target, 'renew')}
                  disabled={saving || activeLocks.length > 0}
                >
                  Renew If Due
                </Button>
              </>
            ) : null}
            <Tooltip title={canReprovision ? 'Re-provision GCP credentials' : 'Credential copy is not configured for this target'}>
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<KeyRound size={14} />}
                  onClick={() => onReprovision(target)}
                  disabled={saving || activeLocks.length > 0 || !canReprovision}
                >
                  Re-provision
                </Button>
              </span>
            </Tooltip>
            <Button
              size="small"
              variant={hasExistingSetup ? 'outlined' : 'contained'}
              startIcon={<Wrench size={14} />}
              onClick={() => onOpenSetup(target)}
              disabled={saving || activeLocks.length > 0}
            >
              {hasExistingSetup ? 'Rerun Setup' : 'Setup'}
            </Button>
            {latestSetup ? (
              <>
                <Tooltip title="View full details">
                  <span>
                    <IconButton size="small" onClick={() => onOpenRunDetails(latestSetup)}>
                      <Eye size={16} />
                    </IconButton>
                  </span>
                </Tooltip>
                {['requested', 'running'].includes(latestSetup.status) ? (
                  <Tooltip title="Refresh status from GitLab">
                    <span>
                      <IconButton size="small" disabled={saving} onClick={() => onRefreshSetup(latestSetup._id)}>
                        <RefreshCcw size={16} />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : null}
              </>
            ) : null}
          </Stack>
        </Stack>

        <Box>
          <Button
            size="small"
            variant="text"
            endIcon={historyOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            onClick={onToggleHistory}
          >
            {historyOpen ? 'Hide History' : 'View History'}
          </Button>
          <Collapse in={historyOpen} unmountOnExit>
            <SetupHistoryPanel
              historyState={historyState}
              onLoadMore={onLoadMoreHistory}
              onOpenRunDetails={(setup) => onOpenRunDetails(setup)}
              onRefreshHistory={onRefreshHistory}
              onRefreshSetup={onRefreshSetup}
              saving={saving}
            />
          </Collapse>
        </Box>
      </Stack>
    </Card>
  );
};
